import React, { useEffect, useState, useCallback } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../utils/dateUtils';
import { employeeManagementApi } from '../api';
import { Btn, Alert, Spinner, EmptyState, RoleBadge } from '../components/ui';

function RespondModal({ request, onClose, onDone }) {
  const [decision,  setDecision]  = useState('APPROVED');
  const [note,      setNote]      = useState('');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const submit = async () => {
    if (decision === 'REJECTED' && !note.trim()) {
      setError('Please provide a reason for rejection'); return;
    }
    setSaving(true); setError('');
    try {
      await employeeManagementApi.respondToDeletion(request.id, decision, note);
      onDone();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to respond');
    } finally { setSaving(false); }
  };

  return (
    <div style={O.overlay}>
      <div style={{ ...O.modal, maxWidth: '440px' }}>
        <div style={O.header}>
          <h3 style={O.title}>Respond to Deletion Request</h3>
          <button onClick={onClose} style={O.close}>×</button>
        </div>

        <div style={{ padding: '14px', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', marginBottom: '16px' }}>
          <p style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>{request.employee?.name}</p>
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
            {request.employee?.employeeId} · Requested by {request.requestedBy?.name}
          </p>
          {request.reason && (
            <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '6px', fontStyle: 'italic' }}>
              "{request.reason}"
            </p>
          )}
        </div>

        {error && <Alert type="error" message={error} />}

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {['APPROVED', 'REJECTED'].map(d => (
            <button key={d} onClick={() => setDecision(d)} style={{
              flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600,
              background: decision === d ? (d === 'APPROVED' ? 'var(--color-success)' : 'var(--color-danger)') : 'var(--color-surface)',
              color:      decision === d ? '#fff' : 'var(--color-text-secondary)',
              border:     `1.5px solid ${decision === d ? (d === 'APPROVED' ? 'var(--color-success)' : 'var(--color-danger)') : 'var(--color-border)'}`,
            }}>{d}</button>
          ))}
        </div>

        {decision === 'APPROVED' && (
          <div style={{ padding: '10px 14px', background: '#FFF5F5', border: '1px solid #FEB2B2', borderRadius: 'var(--radius-sm)', marginBottom: '14px', fontSize: '12px', color: 'var(--color-danger)' }}>
            Approving will deactivate this employee immediately. Their history is preserved permanently.
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={O.label}>
            {decision === 'APPROVED' ? 'Note (optional)' : 'Rejection reason *'}
          </label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder={decision === 'APPROVED' ? 'Optional note...' : 'Why is this request rejected?'}
            rows={2} style={{ ...O.input, resize: 'vertical', marginTop: '6px' }} />
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn
            variant={decision === 'APPROVED' ? 'primary' : 'danger'}
            onClick={submit} loading={saving}
          >
            {decision === 'APPROVED' ? 'Approve Deletion' : 'Reject Request'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default function DeletionApprovals() {
  const [requests,  setRequests]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [showAll,   setShowAll]   = useState(false);
  const [responding, setResponding] = useState(null);

  const load = useCallback(() => {
    const fn = showAll
      ? employeeManagementApi.getAllDeletionRequests
      : employeeManagementApi.getPendingDeletions;
    fn()
      .then(r => setRequests(r.data.data || []))
      .catch(() => setError('Failed to load deletion requests'))
      .finally(() => setLoading(false));
  }, [showAll]);

  useEffect(() => { load(); }, [load]);

  const STATUS_COLORS = {
    PENDING:  { bg: '#FFFBEB', color: '#D69E2E', border: '#FAF089' },
    APPROVED: { bg: '#F0FFF4', color: 'var(--color-success)', border: '#9AE6B4' },
    REJECTED: { bg: '#FFF5F5', color: 'var(--color-danger)', border: '#FEB2B2' },
  };

  return (
    <div style={{ padding: '24px', maxWidth: '720px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h1 style={S.title}>Employee Deletion Requests</h1>
          <p style={S.sub}>
            {showAll ? 'All requests' : 'Pending requests only'}
          </p>
        </div>
        <Btn variant="secondary" size="sm" onClick={() => setShowAll(s => !s)}>
          {showAll ? 'Show Pending Only' : 'Show All'}
        </Btn>
      </div>

      {error   && <Alert type="error"   message={error}   />}
      {success && <Alert type="success" message={success} />}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
      ) : requests.length === 0 ? (
        <EmptyState
          icon="👤"
          title={showAll ? 'No deletion requests' : 'No pending requests'}
          subtitle="Deletion requests raised by Admins will appear here for your review."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {requests.map(req => {
            const sc = STATUS_COLORS[req.status] || STATUS_COLORS.PENDING;
            return (
              <div key={req.id} style={{
                background: 'var(--color-surface)', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)', padding: '16px',
                borderLeft: `4px solid ${sc.color}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    {/* Employee info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--color-primary-lighter)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: 700, flexShrink: 0 }}>
                        {req.employee?.name?.[0] || '?'}
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <p style={{ fontWeight: 600, fontSize: '15px' }}>{req.employee?.name}</p>
                          <RoleBadge role={req.employee?.role} />
                        </div>
                        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>{req.employee?.employeeId}</p>
                      </div>
                    </div>

                    {/* Request details */}
                    <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <p>Requested by: <strong>{req.requestedBy?.name}</strong> · {timeAgo(req.createdAt)}</p>
                      {req.reason && <p style={{ fontStyle: 'italic' }}>"{req.reason}"</p>}
                      {req.respondedBy && (
                        <p>Responded by: <strong>{req.respondedBy.name}</strong>
                          {req.responseNote && ` — "${req.responseNote}"`}
                        </p>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                    <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '999px', fontWeight: 700, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                      {req.status}
                    </span>
                    {req.status === 'PENDING' && (
                      <Btn size="sm" onClick={() => setResponding(req)}>Respond</Btn>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {responding && (
        <RespondModal
          request={responding}
          onClose={() => setResponding(null)}
          onDone={() => {
            setResponding(null);
            setSuccess('Response recorded. Employee and requester have been notified.');
            load();
          }}
        />
      )}
    </div>
  );
}

const S = {
  title: { fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '2px' },
  sub:   { fontSize: '13px', color: 'var(--color-text-muted)' },
};
const O = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '16px' },
  modal:   { background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  title:   { fontSize: '17px', fontWeight: 600, color: 'var(--color-primary)' },
  close:   { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--color-text-muted)' },
  input:   { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', color: 'var(--color-text)', background: 'var(--color-surface)' },
  label:   { fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)' },
};
